import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LogoutModal as PortalLogoutModal } from "@/components/PortalLayout";
import SharedFooter from "@/components/SharedFooter";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import { 
  GraduationCap, LogOut, User, Mail, BookOpen, 
  AlertTriangle, Edit2, Shield, Send,
  Phone, CheckCircle, CreditCard, XCircle,
  Bell, BarChart3, Plus, History, TrendingUp,
  Target, Settings, Eye, EyeOff, Lock, Flame,
  Award, RefreshCw, Clock, Calendar, ChevronRight,
  ChevronLeft, FileText, Download, HelpCircle, Filter,
  MessageCircle, Lightbulb, Sparkles, Zap, Home, Menu
} from "lucide-react";

// Import extracted tab components
import { OverviewTab, SubscriptionTab, AssessmentsTab, AnalyticsTab, SettingsTab } from "../components/parent/tabs";

// Import Child Progress View
import ChildProgressView from "../components/parent/ChildProgressView";

// Import push notification hook
import usePushNotifications from "../hooks/usePushNotifications";

// Import extracted modal components
import { 
  EditChildModal, 
  AddChildModal, 
  WithdrawConsentModal, 
  WithdrawChildConsentModal,
  SupportModal, 
  ChangePasswordModal, 
  ChangePlanModal, 
  TopicSetupModal,
  ReportMismatchModal
} from "../components/parent/modals";

// Import Parent Profile Dropdown
import ParentProfileDropdown from "../components/parent/ParentProfileDropdown";

export default function ParentDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Parent and children data
  const [parent, setParent] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [consentStatus, setConsentStatus] = useState("active");
  
  // Subscription data
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  
  // Assessment history
  const [assessments, setAssessments] = useState([]);
  const [assessmentSummary, setAssessmentSummary] = useState(null);
  const [assessmentPagination, setAssessmentPagination] = useState({ skip: 0, limit: 10, total: 0 });
  
  // Usage analytics
  const [usageStats, setUsageStats] = useState(null);
  
  // Homework credits state
  const [homeworkCredits, setHomeworkCredits] = useState(null);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [selectedCreditPack, setSelectedCreditPack] = useState(null);
  
  // Credit alert preferences
  const [creditAlerts, setCreditAlerts] = useState({
    whatsapp_alerts: true,
    email_alerts: true
  });
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    email_reminders: true,
    whatsapp_reminders: true,
    progress_reports: true,
    subscription_alerts: true,
    // Progress notifications
    session_complete: true,
    daily_limit: true,
    weekly_report: true,
    streak_milestones: true,
    inactivity_reminder: true
  });
  
  // Language preference
  const [languagePreference, setLanguagePreference] = useState("english");
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  
  // Push notification hook
  const pushNotification = usePushNotifications();
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showWithdrawChildModal, setShowWithdrawChildModal] = useState(false);
  const [childToWithdraw, setChildToWithdraw] = useState(null);
  const [withdrawChildLoading, setWithdrawChildLoading] = useState(false);
  const [regrantLoading, setRegrantLoading] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // Form states
  const [editOtpSent, setEditOtpSent] = useState(false);
  const [editOtp, setEditOtp] = useState("");
  const [editForm, setEditForm] = useState({ name: "", age: "", grade: "", school_name: "", subjects_required: "" });
  const [editLoading, setEditLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [supportForm, setSupportForm] = useState({ subject: "", description: "" });
  const [supportLoading, setSupportLoading] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(null);
  
  // Add child form
  const [addChildForm, setAddChildForm] = useState({
    name: "", age: "", grade: "", school_name: "", subjects_required: "", phone: "", board: "cbse"
  });
  const [addChildLoading, setAddChildLoading] = useState(false);
  
  // Change password form
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Plan change modal
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [changePlanLoading, setChangePlanLoading] = useState(false);
  
  // Topic setup modal
  const [showTopicSetupModal, setShowTopicSetupModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({ subject: "", topic: "", description: "" });
  const [reportLoading, setReportLoading] = useState(false);

  const submitMismatchReport = async () => {
    if (!selectedChild?.id || !reportForm.description.trim()) return;
    setReportLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/report-content-mismatch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ child_id: selectedChild.id, ...reportForm })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || "Report submitted");
        setShowReportModal(false);
        setReportForm({ subject: "", topic: "", description: "" });
      } else {
        toast.error(data.detail || "Failed to submit report");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setReportLoading(false);
    }
  };
  const [topicSetupLoading, setTopicSetupLoading] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState({});
  const [customTopics, setCustomTopics] = useState({});
  const [newCustomTopic, setNewCustomTopic] = useState("");
  const [activeTopicSubject, setActiveTopicSubject] = useState(null);
  
  // Available topics by subject
  const TOPICS_BY_SUBJECT = {
    math: [
      "Number Systems", "Fractions & Decimals", "Percentages", "Ratio & Proportion",
      "Basic Algebra", "Linear Equations", "Geometry Basics", "Mensuration",
      "Data Handling", "Probability Basics"
    ],
    science: [
      "Living World", "Plant Biology", "Human Body Systems", "Animal Kingdom",
      "Matter & Materials", "Physical & Chemical Changes", "Force & Motion",
      "Light & Sound", "Electricity & Magnetism", "Environment & Ecology"
    ],
    english: [
      "Grammar Fundamentals", "Tenses", "Vocabulary Building", "Reading Comprehension",
      "Writing Skills", "Creative Writing", "Poetry & Literature", "Speech & Communication"
    ],
    social_studies: [
      "Ancient History", "Medieval History", "Modern History", "Indian Geography",
      "World Geography", "Civics & Government", "Economics Basics", "Maps & Globes"
    ],
    hindi: [
      "व्याकरण (Grammar)", "गद्य (Prose)", "पद्य (Poetry)", "निबंध लेखन (Essay Writing)",
      "पत्र लेखन (Letter Writing)", "मुहावरे और लोकोक्तियाँ", "शब्द भंडार"
    ]
  };
  
  const SUBJECT_NAMES = {
    math: "Mathematics",
    science: "Science", 
    english: "English Grammar",
    hindi: "Hindi Vyakaran"
  };

  // Handle browser back button - show logout confirmation
  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
      setShowLogoutModal(true);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchSupportedLanguages();
    fetchCreditAlerts();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchChildAssessments();
      fetchUsageStats();
      fetchChildLanguagePreference();
      fetchHomeworkCredits();
    }
  }, [selectedChild]);

  // Fetch supported languages
  const fetchSupportedLanguages = async () => {
    try {
      const res = await fetch(`${API}/parent/auth/languages`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSupportedLanguages(data.languages || []);
      }
    } catch (error) {
      console.error("Failed to fetch languages:", error);
    }
  };

  // Fetch child's language preference
  const fetchChildLanguagePreference = async () => {
    if (!selectedChild) return;
    try {
      const res = await fetch(`${API}/parent/auth/child/${selectedChild.id}/language`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLanguagePreference(data.language || "english");
      }
    } catch (error) {
      console.error("Failed to fetch language preference:", error);
    }
  };

  // Update child's language preference
  const updateChildLanguage = async (newLanguage) => {
    if (!selectedChild) {
      toast.error("Please select a child first");
      return;
    }
    try {
      const res = await fetch(`${API}/parent/auth/child/${selectedChild.id}/language`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ language: newLanguage })
      });
      if (res.ok) {
        const data = await res.json();
        setLanguagePreference(newLanguage);
        toast.success(data.message || "Language updated successfully");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to update language");
      }
    } catch (error) {
      console.error("Language update error:", error);
      toast.error("Failed to update language");
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API}/parent/auth/dashboard`, { credentials: "include" });
      if (res.status === 401) {
        navigate("/");
        return;
      }
      const data = await res.json();
      if (data.success) {
        setParent(data.parent);
        // Handle single child or multiple children
        const childList = data.children || (data.child ? [data.child] : []);
        setChildren(childList);
        if (childList.length > 0) {
          setSelectedChild(childList[0]);
          setEditForm({
            name: childList[0].name || "",
            age: childList[0].age?.toString() || "",
            grade: childList[0].grade?.toString() || "",
            school_name: childList[0].school_name || "",
            subjects_required: childList[0].subjects_required || "",
            board: childList[0].board || "cbse"
          });
        }
        setConsentStatus(data.consent_status);
        setSubscription(data.subscription || null);
        setPaymentHistory(data.payment_history || []);
        setNotifications(data.notifications || notifications);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchChildAssessments = async () => {
    if (!selectedChild?.id) return;
    try {
      const res = await fetch(
        `${API}/assessments/student/${selectedChild.id}/history?skip=${assessmentPagination.skip}&limit=${assessmentPagination.limit}`
      );
      const data = await res.json();
      if (data.success) {
        setAssessments(data.assessments);
        setAssessmentSummary(data.summary);
        setAssessmentPagination(prev => ({ ...prev, total: data.pagination.total }));
      }
    } catch (error) {
      console.error("Failed to fetch assessments:", error);
    }
  };

  const fetchUsageStats = async () => {
    if (!selectedChild?.id) return;
    try {
      // Fetch streak data
      const streakRes = await fetch(`${API}/assessments/student/${selectedChild.id}/streak`);
      const streakData = await streakRes.json();
      
      setUsageStats({
        current_streak: streakData.current_streak || 0,
        longest_streak: streakData.longest_streak || 0,
        total_active_days: streakData.total_active_days || 0,
        last_activity: streakData.last_activity,
        daily_average_minutes: selectedChild.session_usage?.daily_minutes_used || 0,
        total_sessions: selectedChild.total_questions || 0
      });
    } catch (error) {
      console.error("Failed to fetch usage stats:", error);
    }
  };

  // Fetch homework help credits balance
  const fetchHomeworkCredits = async () => {
    if (!selectedChild?.id) return;
    try {
      const res = await fetch(`${API}/homework/credits/${selectedChild.id}`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setHomeworkCredits(data);
      }
    } catch (error) {
      console.error("Failed to fetch homework credits:", error);
    }
  };

  // Credit packs for top-up
  const CREDIT_PACKS = [
    { id: "pack_25", credits: 25, price: 75, perCredit: 3, popular: false },
    { id: "pack_50", credits: 50, price: 125, perCredit: 2.5, popular: true },
    { id: "pack_100", credits: 100, price: 200, perCredit: 2, popular: false }
  ];

  // Handle credit top-up purchase
  const handleTopUpPurchase = async () => {
    if (!selectedCreditPack || !selectedChild?.id) {
      toast.error("Please select a credit pack");
      return;
    }
    
    setTopUpLoading(true);
    try {
      const res = await fetch(`${API}/homework/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          child_id: selectedChild.id,
          package_id: selectedCreditPack.id,
          credits: selectedCreditPack.credits,
          amount: selectedCreditPack.price
        })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast.success(`${selectedCreditPack.credits} credits added successfully!`);
        setShowTopUpModal(false);
        setSelectedCreditPack(null);
        // Refresh credits
        fetchHomeworkCredits();
      } else {
        toast.error(data.detail || "Failed to purchase credits");
      }
    } catch (error) {
      console.error("Credit purchase error:", error);
      toast.error("Failed to complete purchase");
    } finally {
      setTopUpLoading(false);
    }
  };

  // Fetch credit alert preferences
  const fetchCreditAlerts = async () => {
    try {
      const res = await fetch(`${API}/auth/notification-preferences`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setCreditAlerts({
          whatsapp_alerts: data.whatsapp_alerts ?? true,
          email_alerts: data.email_alerts ?? true
        });
      }
    } catch (error) {
      console.error("Failed to fetch credit alerts:", error);
    }
  };

  // Update credit alert preference
  const updateCreditAlert = async (key, value) => {
    try {
      const res = await fetch(`${API}/auth/notification-preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [key]: value })
      });
      
      if (res.ok) {
        setCreditAlerts(prev => ({ ...prev, [key]: value }));
        toast.success(value ? "Alerts enabled" : "Alerts disabled");
      } else {
        toast.error("Failed to update preference");
      }
    } catch (error) {
      console.error("Failed to update credit alert:", error);
      toast.error("Network error");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/parent/auth/logout`, { method: "POST", credentials: "include" });
      setShowLogoutModal(false);
      navigate("/");
    } catch {
      setShowLogoutModal(false);
      navigate("/");
    }
  };
  
  const confirmLogout = () => {
    setShowLogoutModal(true);
  };

  const selectChild = (child) => {
    setSelectedChild(child);
    setEditForm({
      name: child.name || "",
      age: child.age?.toString() || "",
      grade: child.grade?.toString() || "",
      school_name: child.school_name || "",
      subjects_required: child.subjects_required || "",
      board: child.board || "cbse"
    });
    setAssessmentPagination(prev => ({ ...prev, skip: 0 }));
  };

  // Switch a child's curriculum board (instant, no OTP)
  const updateChildBoard = async (newBoard) => {
    if (!selectedChild?.id) return;
    setEditForm((prev) => ({ ...prev, board: newBoard }));
    try {
      const res = await fetch(`${API}/parent/auth/child/${selectedChild.id}/board`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ board: newBoard })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || "Board updated");
        fetchDashboard();
      } else {
        toast.error(data.detail || "Failed to update board");
      }
    } catch {
      toast.error("Network error");
    }
  };

  // Edit child details
  const requestEditOTP = async () => {
    setEditLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/request-update-otp`, {
        method: "POST",
        credentials: "include"
      });
      const data = await res.json();
      if (data.success) {
        setEditOtpSent(true);
        toast.success("Verification code sent to your email");
      } else {
        toast.error(data.detail || "Failed to send code");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setEditLoading(false);
    }
  };

  const submitEditChanges = async () => {
    if (!editOtp || editOtp.length !== 6) {
      toast.error("Please enter the 6-digit verification code");
      return;
    }
    setEditLoading(true);
    try {
      const updates = {};
      if (editForm.name !== selectedChild?.name) updates.name = editForm.name;
      if (editForm.age && parseInt(editForm.age) !== selectedChild?.age) updates.age = parseInt(editForm.age);
      if (editForm.grade && parseInt(editForm.grade) !== selectedChild?.grade) updates.grade = parseInt(editForm.grade);
      if (editForm.school_name !== selectedChild?.school_name) updates.school_name = editForm.school_name;
      if (editForm.subjects_required !== selectedChild?.subjects_required) updates.subjects_required = editForm.subjects_required;
      
      if (Object.keys(updates).length === 0) {
        toast.info("No changes to save");
        return;
      }
      
      const res = await fetch(`${API}/parent/auth/update-child`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ otp_code: editOtp, updates, child_id: selectedChild.id })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success("Details updated successfully");
        setShowEditModal(false);
        setEditOtpSent(false);
        setEditOtp("");
        fetchDashboard();
      } else {
        toast.error(data.detail || "Update failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setEditLoading(false);
    }
  };

  // Withdraw consent
  const handleWithdrawConsent = async () => {
    setWithdrawLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/withdraw-consent`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ child_id: selectedChild?.id })
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Consent withdrawn successfully");
        setShowWithdrawModal(false);
        setTimeout(() => navigate("/"), 2000);
      } else {
        toast.error(data.detail || "Failed to withdraw consent");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Per-child consent withdrawal (soft, 30-day grace)
  const handleWithdrawChild = (child) => {
    setChildToWithdraw(child);
    setShowWithdrawChildModal(true);
  };

  const confirmWithdrawChild = async () => {
    if (!childToWithdraw?.id) return;
    setWithdrawChildLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/child/${childToWithdraw.id}/withdraw-consent`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "parent_dashboard" })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || "Consent withdrawn");
        setShowWithdrawChildModal(false);
        setChildToWithdraw(null);
        fetchDashboard();
      } else {
        toast.error(data.detail || "Failed to withdraw consent");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setWithdrawChildLoading(false);
    }
  };

  const handleRegrantChild = async (child) => {
    if (!child?.id) return;
    setRegrantLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/child/${child.id}/regrant-consent`, {
        method: "POST",
        credentials: "include"
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || "Consent re-granted");
        fetchDashboard();
      } else {
        toast.error(data.detail || "Failed to re-grant consent");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setRegrantLoading(false);
    }
  };

  // Support form
  const handleSupportSubmit = async () => {
    if (!supportForm.subject.trim() || !supportForm.description.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setSupportLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/support-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(supportForm)
      });
      const data = await res.json();
      if (data.success) {
        setTicketSubmitted(data.ticket_id);
        toast.success("Support request submitted");
      } else {
        toast.error(data.detail || "Failed to submit");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSupportLoading(false);
    }
  };

  // Add new child
  const handleAddChild = async () => {
    if (!addChildForm.name || !addChildForm.age || !addChildForm.grade || !addChildForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    setAddChildLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/add-child`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(addChildForm)
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Child added successfully!");
        setShowAddChildModal(false);
        setAddChildForm({ name: "", age: "", grade: "", school_name: "", subjects_required: "", phone: "", board: "cbse" });
        fetchDashboard();
      } else {
        toast.error(data.detail || "Failed to add child");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setAddChildLoading(false);
    }
  };

  // Update notification preferences
  const updateNotificationPref = async (key, value) => {
    const newPrefs = { ...notifications, [key]: value };
    setNotifications(newPrefs);
    try {
      await fetch(`${API}/parent/auth/update-notifications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newPrefs)
      });
      toast.success("Preferences updated");
    } catch {
      toast.error("Failed to update preferences");
    }
  };

  const handleChangePassword = async () => {
    // Validate form
    if (!passwordForm.currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!passwordForm.newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      toast.error("New password must be different from current password");
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await fetch(`${API}/parent/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword
        })
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success("Password changed successfully");
        setShowChangePasswordModal(false);
        setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        toast.error(data.detail || "Failed to change password");
      }
    } catch (error) {
      toast.error("Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle plan change
  const handleChangePlan = async () => {
    if (!selectedPlan || !selectedChild?.phone) {
      toast.error("Please select a plan");
      return;
    }
    
    // Check if already on this plan
    const currentPlan = subscription?.plan_type || "free_trial";
    if (currentPlan === selectedPlan) {
      toast.info("You are already on this plan");
      return;
    }
    
    setChangePlanLoading(true);
    try {
      const res = await fetch(`${API}/subscription/change-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          child_phone: selectedChild.phone,
          new_plan: selectedPlan
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(data.message || "Plan updated successfully!");
        setShowChangePlanModal(false);
        setSelectedPlan(null);
        fetchDashboard(); // Refresh data
      } else {
        toast.error(data.detail || "Failed to change plan");
      }
    } catch (error) {
      toast.error("Failed to change plan");
    } finally {
      setChangePlanLoading(false);
    }
  };

  // Get plan details for display
  const getPlanDetails = (planType) => {
    const plans = {
      free_trial: { name: "Free Trial", price: 0, color: "gray", dailyLimit: "1 hour" },
      starter: { name: "Starter", price: 499, color: "emerald", dailyLimit: "1 hour" },
      plus: { name: "Plus", price: 699, color: "blue", dailyLimit: "2 hours" },
      pro: { name: "Pro", price: 899, color: "purple", dailyLimit: "Unlimited" }
    };
    return plans[planType] || plans.free_trial;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric"
    });
  };

  const getSubscriptionStatus = () => {
    if (!subscription) return { status: "none", color: "gray", text: "No Subscription" };
    if (subscription.status === "active") return { status: "active", color: "green", text: "Active" };
    if (subscription.status === "grace_period") return { status: "grace", color: "yellow", text: "Grace Period" };
    if (subscription.status === "expired") return { status: "expired", color: "red", text: "Expired" };
    return { status: "unknown", color: "gray", text: subscription.status };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const subStatus = getSubscriptionStatus();

  // Function to navigate to a specific tab from the dropdown
  const navigateToTab = (tab) => {
    setActiveTab(tab);
  };

  // Right content for header - EduAssist Credits + Messages button + Profile dropdown
  const headerRightContent = (
    <div className="flex items-center gap-2">
      {/* EduAssist Credits Badge */}
      <div 
        className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 rounded-full cursor-pointer hover:bg-purple-200 transition-colors"
        onClick={() => navigateToTab('overview')}
        data-testid="eduassist-credits-header"
      >
        <Lightbulb className="h-4 w-4 text-purple-600" />
        <span className="text-sm font-semibold text-purple-700">
          {homeworkCredits?.remaining_credits ?? 15}
        </span>
        <span className="text-xs text-purple-500">credits</span>
      </div>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => navigate("/parent/messages")}
        className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200 hidden sm:flex"
        data-testid="messages-button"
      >
        <MessageCircle className="h-4 w-4 mr-1.5" />
        Messages
      </Button>
      <ParentProfileDropdown
        parent={parent}
        subscription={subscription}
        onLogout={confirmLogout}
        onNavigateToTab={navigateToTab}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col">
      {/* Unified Header matching landing page */}
      <UnifiedPortalHeader
        portalType="parent"
        userName=""
        userSubtitle=""
        onLogout={confirmLogout}
        rightContent={headerRightContent}
        showUserInfo={false}
        showHomeButton={false}
        hideLogout={true}
      />

      <main className="w-[80%] mx-auto py-8 flex-1">
        {/* Child Selector (if multiple children) */}
        {children.length > 1 && (
          <Card className="mb-6">
            <CardContent className="py-5">
              <div className="flex items-center gap-4 overflow-x-auto pb-2">
                <span className="text-sm font-medium text-gray-600 whitespace-nowrap">Select Child:</span>
                {children.map((child) => (
                  <Button
                    key={child.id}
                    variant={selectedChild?.id === child.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => selectChild(child)}
                    className={selectedChild?.id === child.id ? "bg-blue-600" : ""}
                  >
                    <User className="h-4 w-4 mr-2" />
                    {child.name}
                  </Button>
                ))}
                <Button variant="ghost" size="sm" onClick={() => setShowAddChildModal(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Child
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Tabs - Clean single row layout */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-overview">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="progress" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-progress">
              <TrendingUp className="h-4 w-4 mr-2" />
              Progress
            </TabsTrigger>
            <TabsTrigger value="assessments" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-assessments">
              <History className="h-4 w-4 mr-2" />
              Assessments
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-analytics">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <OverviewTab
              selectedChild={selectedChild}
              usageStats={usageStats}
              subscription={subscription}
              subStatus={subStatus}
              assessments={assessments}
              formatDate={formatDate}
              setShowEditModal={setShowEditModal}
              setActiveTab={setActiveTab}
              homeworkCredits={homeworkCredits}
              setShowTopUpModal={setShowTopUpModal}
              setShowReportModal={setShowReportModal}
            />
          </TabsContent>

          {/* Progress Tab - New Child Progress View with Charts */}
          <TabsContent value="progress" className="space-y-6">
            {selectedChild ? (
              <ChildProgressView 
                childId={selectedChild.id} 
                childName={selectedChild.name}
              />
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Please select a child to view their progress</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription" className="space-y-6">
            <SubscriptionTab
              selectedChild={selectedChild}
              childrenList={children}
              selectChild={selectChild}
              subscription={subscription}
              subStatus={subStatus}
              paymentHistory={paymentHistory}
              formatDate={formatDate}
              getPlanDetails={getPlanDetails}
              setShowChangePlanModal={setShowChangePlanModal}
              setSelectedPlan={setSelectedPlan}
            />
          </TabsContent>

          {/* Assessments Tab */}
          <TabsContent value="assessments" className="space-y-6">
            <AssessmentsTab
              assessments={assessments}
              assessmentSummary={assessmentSummary}
              assessmentPagination={assessmentPagination}
              setAssessmentPagination={setAssessmentPagination}
              formatDate={formatDate}
            />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <AnalyticsTab
              usageStats={usageStats}
              assessmentSummary={assessmentSummary}
            />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <SettingsTab
              notifications={notifications}
              updateNotificationPref={updateNotificationPref}
              parent={parent}
              consentStatus={consentStatus}
              formatDate={formatDate}
              setShowChangePasswordModal={setShowChangePasswordModal}
              setShowWithdrawModal={setShowWithdrawModal}
              setShowSupportModal={setShowSupportModal}
              selectedChild={selectedChild}
              onWithdrawChild={handleWithdrawChild}
              onRegrantChild={handleRegrantChild}
              withdrawChildLoading={withdrawChildLoading}
              regrantLoading={regrantLoading}
              languagePreference={languagePreference}
              onLanguageChange={updateChildLanguage}
              supportedLanguages={supportedLanguages}
              onDownloadData={async () => {
                if (!selectedChild) {
                  toast.error("Please select a child first");
                  return;
                }
                try {
                  const response = await fetch(`${API}/parent/auth/child/${selectedChild.id}/data-export/download`, {
                    credentials: 'include'
                  });
                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `eduzy_data_export_${selectedChild.student_id}_${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    toast.success("Data exported successfully");
                  } else {
                    toast.error("Failed to export data");
                  }
                } catch (error) {
                  toast.error("Export failed");
                }
              }}
              creditAlerts={creditAlerts}
              onCreditAlertChange={updateCreditAlert}
              pushNotification={pushNotification}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      <EditChildModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        editForm={editForm}
        setEditForm={setEditForm}
        editOtpSent={editOtpSent}
        editOtp={editOtp}
        setEditOtp={setEditOtp}
        editLoading={editLoading}
        requestEditOTP={requestEditOTP}
        submitEditChanges={submitEditChanges}
        updateChildBoard={updateChildBoard}
      />

      <AddChildModal
        open={showAddChildModal}
        onOpenChange={setShowAddChildModal}
        addChildForm={addChildForm}
        setAddChildForm={setAddChildForm}
        addChildLoading={addChildLoading}
        handleAddChild={handleAddChild}
      />

      <WithdrawConsentModal
        open={showWithdrawModal}
        onOpenChange={setShowWithdrawModal}
        withdrawLoading={withdrawLoading}
        handleWithdrawConsent={handleWithdrawConsent}
      />

      <WithdrawChildConsentModal
        open={showWithdrawChildModal}
        onOpenChange={setShowWithdrawChildModal}
        child={childToWithdraw}
        loading={withdrawChildLoading}
        onConfirm={confirmWithdrawChild}
      />

      <SupportModal
        open={showSupportModal}
        onOpenChange={setShowSupportModal}
        supportForm={supportForm}
        setSupportForm={setSupportForm}
        supportLoading={supportLoading}
        ticketSubmitted={ticketSubmitted}
        setTicketSubmitted={setTicketSubmitted}
        handleSupportSubmit={handleSupportSubmit}
      />

      <ChangePasswordModal
        open={showChangePasswordModal}
        onOpenChange={setShowChangePasswordModal}
        passwordForm={passwordForm}
        setPasswordForm={setPasswordForm}
        passwordLoading={passwordLoading}
        showCurrentPassword={showCurrentPassword}
        setShowCurrentPassword={setShowCurrentPassword}
        showNewPassword={showNewPassword}
        setShowNewPassword={setShowNewPassword}
        handleChangePassword={handleChangePassword}
      />

      <ChangePlanModal
        open={showChangePlanModal}
        onOpenChange={setShowChangePlanModal}
        selectedChild={selectedChild}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        subscription={subscription}
        getPlanDetails={getPlanDetails}
        changePlanLoading={changePlanLoading}
        handleChangePlan={handleChangePlan}
      />

      <TopicSetupModal
        open={showTopicSetupModal}
        onOpenChange={setShowTopicSetupModal}
        selectedChild={selectedChild}
        activeTopicSubject={activeTopicSubject}
        setActiveTopicSubject={setActiveTopicSubject}
        selectedTopics={selectedTopics}
        setSelectedTopics={setSelectedTopics}
        customTopics={customTopics}
        setCustomTopics={setCustomTopics}
        newCustomTopic={newCustomTopic}
        setNewCustomTopic={setNewCustomTopic}
        topicSetupLoading={topicSetupLoading}
        onSave={async () => {
          setTopicSetupLoading(true);
          try {
            const res = await fetch(`${API}/parent/auth/child/${selectedChild.id}/topics`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                selected_topics: selectedTopics,
                custom_topics: customTopics
              })
            });
            const data = await res.json();
            if (data.success) {
              toast.success("Learning topics saved successfully!");
              setShowTopicSetupModal(false);
              fetchDashboard();
            } else {
              toast.error(data.detail || "Failed to save topics");
            }
          } catch {
            toast.error("Failed to save topics");
          } finally {
            setTopicSetupLoading(false);
          }
        }}
      />

      <ReportMismatchModal
        open={showReportModal}
        onOpenChange={setShowReportModal}
        child={selectedChild}
        form={reportForm}
        setForm={setReportForm}
        loading={reportLoading}
        onSubmit={submitMismatchReport}
      />

      {/* Logout Confirmation Modal - Using Portal Styled Modal */}
      <PortalLogoutModal
        open={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        onConfirm={handleLogout}
        portalType="parent"
      />

      {/* Credit Top-Up Modal */}
      <Dialog open={showTopUpModal} onOpenChange={setShowTopUpModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-purple-600" />
              Top Up Homework Credits
            </DialogTitle>
            <DialogDescription>
              Purchase additional homework help credits for {selectedChild?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Balance */}
            {homeworkCredits && (
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-purple-700">Current Balance</span>
                  <span className="text-2xl font-bold text-purple-700">
                    {homeworkCredits.remaining_credits} credits
                  </span>
                </div>
                {homeworkCredits.is_free_trial && homeworkCredits.free_trial_remaining > 0 && (
                  <p className="text-xs text-purple-600 mt-1">
                    Includes {homeworkCredits.free_trial_remaining} free trial credits
                  </p>
                )}
              </div>
            )}
            
            {/* Credit Packs */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Select a credit pack:</p>
              {CREDIT_PACKS.map((pack) => (
                <div
                  key={pack.id}
                  onClick={() => setSelectedCreditPack(pack)}
                  className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedCreditPack?.id === pack.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  {pack.popular && (
                    <span className="absolute -top-2 right-3 bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      BEST VALUE
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{pack.credits} Credits</p>
                      <p className="text-xs text-gray-500">₹{pack.perCredit}/credit</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-900">₹{pack.price}</p>
                      <p className="text-xs text-gray-500">+ 18% GST</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              Credits are valid for 30 days from purchase. Follow-up questions are always free!
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTopUpModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleTopUpPurchase}
              disabled={!selectedCreditPack || topUpLoading}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-topup-btn"
            >
              {topUpLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Purchase {selectedCreditPack?.credits || 0} Credits
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unified Footer matching landing page */}
      <SharedFooter />
    </div>
  );
}
